/**
 * What this install calls itself when it registers as a device.
 *
 * `POST /api/me/encryption/devices` takes `{ id, publicKey, name, platform }`
 * (contract section 4). Two of those four are this module's business:
 *
 * - `platform` — one of `ios`, `android`, `web` (`DEVICE_PLATFORMS`); anything
 *   else is a 400 from the DTO's `@IsIn`. `web` is not in the spec's device
 *   list: the API accepts it so the Playwright build, which runs as a browser,
 *   can register under the test-only `courses-notes-web` flag (spec 6.10).
 * - `name` — a human label, shown to the member on the approving device's "New
 *   device request" screen and on the Devices screen. Capped at 100 characters
 *   by `EnrolDeviceDto`.
 *
 * The PERSISTED device id and its P-256 key pair are NOT this module's: they
 * belong to the key store and are read through `courses-keys/deviceIdentity`
 * (spec 6.2, "Startup order"). `newDeviceId()` here is the fallback that mints
 * one — for the first registration on an install, and for the case the
 * "Approve this device" screen has to handle when a request was denied, since
 * the API never upgrades a `REVOKED` row and the device must come back under a
 * new identity.
 *
 * No cryptography lives here beyond asking the platform for a UUID; key
 * generation is `courses-crypto/device-key.js` (#1539).
 */
import { Device } from '@capacitor/device';
import { isAndroidNativeOnly, isIosNativeOnly, isNativeNotWeb } from '@/utils/platform-check';

/** What the API's `DEVICE_PLATFORMS` accepts, in contract order. */
export const DEVICE_PLATFORMS = Object.freeze(['ios', 'android', 'web']);

/** `EnrolDeviceDto`: `@IsString() @IsNotEmpty() @MaxLength(100)`. */
export const DEVICE_NAME_MAX_LENGTH = 100;

/**
 * When the platform can tell us nothing useful. On the web build `getInfo()`
 * carries no `name` and its `model` is the browser engine, which would mean
 * nothing to a member reading an approval request.
 */
export const FALLBACK_DEVICE_NAME = 'Web browser';

/**
 * The `platform` this install registers under.
 *
 * Web is decided first and on its own: mobile Safari answers
 * `isPlatform('ios')`, so asking the native-only checks first would label a
 * browser `ios`. A native shell that claims neither platform falls back to
 * `web` rather than sending a value the DTO would refuse.
 *
 * @returns {'ios' | 'android' | 'web'}
 */
export function thisPlatform() {
  if (!isNativeNotWeb()) return 'web';
  if (isIosNativeOnly()) return 'ios';
  if (isAndroidNativeOnly()) return 'android';
  return 'web';
}

/**
 * @param {unknown} value
 * @returns {string} trimmed and capped at what the register DTO accepts, or ''
 */
function label(value) {
  if (typeof value !== 'string') return '';
  return value.trim().slice(0, DEVICE_NAME_MAX_LENGTH);
}

/**
 * This install's platform and human label, ready to spread into a register or
 * enrol body.
 *
 * Never rejects: the label is text on a screen and the platform is decided
 * without the plugin, so a device plugin that throws must not be what stops a
 * member from registering.
 *
 * @returns {Promise<{ platform: 'ios'|'android'|'web', name: string }>}
 */
export async function describeThisDevice() {
  const platform = thisPlatform();
  let info = null;
  try {
    info = await Device.getInfo();
  } catch (error) {
    console.warn('[CoursesEncryption] Could not read the device info:', error);
  }
  const name = label(info?.name) || label(info?.model) || FALLBACK_DEVICE_NAME;
  return { platform, name };
}

/**
 * A fresh device id. The API validates it with `@IsUUID('4')`, so this is the
 * platform's own UUID and never a hand-rolled one: an id that does not parse is
 * a 400 on register, and `X-Device-Id` is silently ignored (treated as absent)
 * when it is not a v4, which would leave `thisDevice` null on every status read
 * with nothing to show for it.
 *
 * @returns {string} a UUID v4
 * @throws {Error} when the runtime has no `crypto.randomUUID`
 */
export function newDeviceId() {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (typeof randomUUID !== 'function') {
    throw new Error('This device cannot generate an id: crypto.randomUUID is unavailable');
  }
  return globalThis.crypto.randomUUID();
}
